import {
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type AttachmentGridImage,
    NCoreUIKitTheme,
    Attachment,
    Button
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Music as MusicIcon
} from "lucide-react-native";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import ncoreLogoDark from "../../assets/ncore-logo-preview-dark.png";
import ncoreLogo from "../../assets/ncore-logo-preview.png";

type UploadPhase = "uploading" | "processing" | "done";

const NAME = "Attachment";

const USAGE_CODE = `<Attachment
    uploadState="uploading"
    title="report.pdf"
    onRemove={cancel}
    progress={64}
/>

<Attachment
    uploadState="done"
    description="1.2 MB"
    title="report.pdf"
    onRemove={remove}
    onPress={open}
/>`;

const MEDIA_CODE = `<Attachment
    orientation="vertical"
    title="clip.mp4"
    source={poster}
    onPress={open}
    badge="0:42"
/>

<Attachment
    title="voice-note.wav"
    icon={MusicIcon}
/>`;

const GROUP_CODE = `<Attachment.Group
    layoutBehaviour="scroll"
    isInField={true}
    title="Files"
>
    {files.map((file) => <Attachment
        onRemove={() => remove(file.id)}
        title={file.name}
        key={file.id}
    />)}
</Attachment.Group>`;

const GRID_CODE = `<Attachment.Grid
    images={photos}
    onPress={open}
    maxVisible={4}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const GRID_COUNTS = [
    2,
    3,
    5
] as const;

const GROUP_FILES = [
    {
        description: "sample.fileSize",
        title: "sample.fileName",
        id: "report"
    },
    {
        description: "attachmentPage.sheetSize",
        title: "attachmentPage.fileSheet",
        id: "sheet"
    },
    {
        description: "attachmentPage.dataSize",
        title: "attachmentPage.fileData",
        id: "data"
    }
];

const PROGRESS_STEP = 20;

const PROGRESS_DELAY = 450;

const PROCESS_DELAY = 1300;

const FIELD_STYLE = {
    maxWidth: "100%"
};

const FieldFrame = ({
    isNarrow = false,
    children
}: {
    children: ReactNode;
    isNarrow?: boolean;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            ...FIELD_STYLE,
            width: isNarrow ? shapeConfigs.drawerWidth : shapeConfigs.dialogMaxWidth
        }}
    >
        <DemoFrame>
            <View
                style={{
                    padding: spaces.spacingSm
                }}
            >
                {children}
            </View>
        </DemoFrame>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("attachmentPage.mediaTitle"),
        id: "media"
    },
    {
        title: t("attachmentPage.groupTitle"),
        id: "group"
    },
    {
        title: t("attachmentPage.gridTitle"),
        id: "grid"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AttachmentPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeTheme
    } = NCoreUIKitTheme.useContext();

    const [
        phase,
        setPhase
    ] = useState<UploadPhase>("done");

    const [
        progress,
        setProgress
    ] = useState(100);

    const [
        isRemoved,
        setIsRemoved
    ] = useState(false);

    const [
        groupFiles,
        setGroupFiles
    ] = useState(GROUP_FILES);

    const [
        openedImage,
        setOpenedImage
    ] = useState<number | undefined>(undefined);

    useEffect(() => {
        if(phase === "done") {
            return;
        }

        const timer = window.setTimeout(() => {
            if(phase === "processing") {
                setPhase("done");

                return;
            }

            if(progress >= 100) {
                setPhase("processing");

                return;
            }

            setProgress(progress + PROGRESS_STEP);
        }, phase === "processing" ? PROCESS_DELAY : PROGRESS_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        progress,
        phase
    ]);

    const handleUpload = () => {
        setIsRemoved(false);

        setProgress(0);

        setPhase("uploading");
    };

    const handleRemove = () => {
        setIsRemoved(true);

        setPhase("done");
    };

    const handleGroupRemove = (id: string) => setGroupFiles((current) => current.filter((file) => file.id !== id));

    const handleGroupRestore = () => setGroupFiles(GROUP_FILES);

    const handleGridPress = (index: number) => setOpenedImage(index);

    const logo = {
        uri: activeTheme.indexOf("dark") === -1 ? ncoreLogo : ncoreLogoDark
    };

    const getGridImages = (count: number): Array<AttachmentGridImage> => Array.from({
        length: count
    }, (_, index) => ({
        alt: t("attachmentPage.imageAlt", [
            index + 1
        ]),
        source: logo
    }));

    return <Article>
        <ComponentHeader
            lead={t("attachmentPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("attachmentPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("attachmentPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("attachmentPage.overviewCaption")}
            >
                {
                    isRemoved ?
                        null
                    :
                        <Attachment
                            description={phase === "done" ? t("sample.fileSize") : undefined}
                            title={t("sample.fileName")}
                            onRemove={handleRemove}
                            progress={progress}
                            uploadState={phase}
                        />
                }
                <Button
                    isDisabled={!isRemoved && phase !== "done"}
                    title={t("attachmentPage.upload")}
                    onPress={handleUpload}
                    spreadBehaviour="free"
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("attachmentPage.statesText")}
            </Paragraph>
            <Preview>
                <Attachment
                    title={t("attachmentPage.fileSheet")}
                    onRemove={() => undefined}
                    uploadState="idle"
                />
                <Attachment
                    title={t("attachmentPage.fileVideo")}
                    onRemove={() => undefined}
                    uploadState="uploading"
                    progress={64}
                />
                <Attachment
                    errorText={t("attachmentPage.errorReason")}
                    title={t("attachmentPage.fileData")}
                    onRemove={() => undefined}
                    onRetry={() => undefined}
                    uploadState="error"
                />
                <Attachment
                    description={t("sample.fileSize")}
                    title={t("sample.fileName")}
                    onRemove={() => undefined}
                    onPress={() => undefined}
                    uploadState="done"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("attachmentPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <Attachment
                        description={t("sample.fileSize")}
                        title={t("sample.fileName")}
                        onRemove={() => undefined}
                        onPress={() => undefined}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("attachmentPage.mediaTitle")}
            id="media"
        >
            <Paragraph>
                {t("attachmentPage.mediaText")}
            </Paragraph>
            <Preview>
                <Attachment
                    description={t("attachmentPage.sheetSize")}
                    title={t("attachmentPage.fileSheet")}
                />
                <Attachment
                    description={t("attachmentPage.dataSize")}
                    title={t("attachmentPage.fileData")}
                />
                <Attachment
                    description={t("attachmentPage.audioSize")}
                    title={t("attachmentPage.fileAudio")}
                    icon={MusicIcon}
                />
            </Preview>
            <Paragraph>
                {t("attachmentPage.verticalText")}
            </Paragraph>
            <Preview>
                <Attachment
                    description={t("attachmentPage.photoSize")}
                    title={t("attachmentPage.fileImage")}
                    onRemove={() => undefined}
                    onPress={() => undefined}
                    orientation="vertical"
                    source={logo}
                />
                <Attachment
                    description={t("attachmentPage.videoSize")}
                    title={t("attachmentPage.fileVideo")}
                    onRemove={() => undefined}
                    onPress={() => undefined}
                    orientation="vertical"
                    source={logo}
                    badge="0:42"
                />
                <Attachment
                    errorText={t("attachmentPage.errorReason")}
                    title={t("attachmentPage.fileImage")}
                    onRemove={() => undefined}
                    onRetry={() => undefined}
                    orientation="vertical"
                    uploadState="error"
                />
            </Preview>
            <CodeBlock
                code={MEDIA_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("attachmentPage.groupTitle")}
            id="group"
        >
            <Paragraph>
                {t("attachmentPage.groupText")}
            </Paragraph>
            <Preview>
                <FieldFrame>
                    <Attachment.Group
                        layoutBehaviour="wrap"
                        isInField={true}
                    >
                        {
                            groupFiles.map((file) => <Attachment
                                onRemove={() => handleGroupRemove(file.id)}
                                description={t(file.description)}
                                title={t(file.title)}
                                key={file.id}
                            />)
                        }
                    </Attachment.Group>
                </FieldFrame>
                <FieldFrame
                    isNarrow={true}
                >
                    <Attachment.Group
                        layoutBehaviour="scroll"
                        isInField={true}
                    >
                        <Attachment
                            description={t("sample.fileSize")}
                            title={t("sample.fileName")}
                            onRemove={() => undefined}
                        />
                        <Attachment
                            description={t("attachmentPage.sheetSize")}
                            title={t("attachmentPage.fileSheet")}
                            onRemove={() => undefined}
                        />
                        <Attachment
                            description={t("attachmentPage.dataSize")}
                            title={t("attachmentPage.fileData")}
                            onRemove={() => undefined}
                        />
                    </Attachment.Group>
                </FieldFrame>
                <Button
                    isDisabled={groupFiles.length === GROUP_FILES.length}
                    title={t("attachmentPage.groupRestore")}
                    onPress={handleGroupRestore}
                    spreadBehaviour="free"
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("attachmentPage.gridTitle")}
            id="grid"
        >
            <Paragraph>
                {t("attachmentPage.gridText")}
            </Paragraph>
            <Preview
                caption={openedImage === undefined ? t("attachmentPage.gridHint") : t("attachmentPage.gridOpened", [
                    openedImage + 1
                ])}
            >
                {
                    GRID_COUNTS.map((count) => <Attachment.Grid
                        images={getGridImages(count)}
                        onPress={handleGridPress}
                        key={count}
                    />)
                }
            </Preview>
            <CodeBlock
                code={GRID_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("attachmentPage.a11yBusy"),
                    t("attachmentPage.a11yProgress"),
                    t("attachmentPage.a11yActions"),
                    t("attachmentPage.a11yTrigger"),
                    t("attachmentPage.a11yError"),
                    t("attachmentPage.a11yGroup"),
                    t("attachmentPage.a11yGrid"),
                    t("attachmentPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("attachmentPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("attachmentPage.propDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        type: "\"idle\" | \"uploading\" | \"processing\" | \"error\" | \"done\"",
                        description: t("attachmentPage.propUploadState"),
                        defaultValue: "\"done\"",
                        name: "uploadState"
                    },
                    {
                        description: t("attachmentPage.propProgress"),
                        name: "progress",
                        type: "number"
                    },
                    {
                        description: t("attachmentPage.propErrorText"),
                        name: "errorText",
                        type: "string"
                    },
                    {
                        description: t("attachmentPage.propSource"),
                        type: "ImageSourcePropType",
                        name: "source"
                    },
                    {
                        description: t("attachmentPage.propIcon"),
                        type: "LucideIcon",
                        name: "icon"
                    },
                    {
                        description: t("attachmentPage.propBadge"),
                        type: "string",
                        name: "badge"
                    },
                    {
                        description: t("attachmentPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("attachmentPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("attachmentPage.propOnRemove"),
                        type: "() => void",
                        name: "onRemove"
                    },
                    {
                        description: t("attachmentPage.propOnRetry"),
                        type: "() => void",
                        name: "onRetry"
                    },
                    {
                        description: t("attachmentPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("attachmentPage.propIsInField"),
                        name: "isInField",
                        type: "boolean"
                    },
                    {
                        description: t("attachmentPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("attachmentPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("attachmentPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("attachmentPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Attachment.Group
                </InlineCode>
                {" "}
                {t("attachmentPage.groupPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("attachmentPage.groupLayout"),
                        type: "\"scroll\" | \"wrap\"",
                        defaultValue: "\"scroll\"",
                        name: "layoutBehaviour"
                    },
                    {
                        description: t("attachmentPage.groupIsInField"),
                        defaultValue: "false",
                        name: "isInField",
                        type: "boolean"
                    },
                    {
                        description: t("attachmentPage.groupIsInitialAnimated"),
                        name: "isInitialAnimated",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("attachmentPage.groupTitleProp"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Attachment.Grid
                </InlineCode>
                {" "}
                {t("attachmentPage.gridPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "Array<{ source: ImageSourcePropType; alt: string }>",
                        description: t("attachmentPage.gridImages"),
                        name: "images"
                    },
                    {
                        description: t("attachmentPage.gridMaxVisible"),
                        name: "maxVisible",
                        defaultValue: "4",
                        type: "number"
                    },
                    {
                        description: t("attachmentPage.gridOnPress"),
                        type: "(index: number) => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Attachment.Action
                </InlineCode>
                {" "}
                {t("attachmentPage.actionPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("attachmentPage.actionIcon"),
                        type: "LucideIcon",
                        name: "icon"
                    },
                    {
                        description: t("attachmentPage.actionLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("attachmentPage.actionOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("attachmentPage.actionIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("attachmentPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AttachmentPage;
